/**
 * Theme Manager
 * Handles theme switching and persistence
 */

const THEME_KEY = 'user_theme_preference';
const THEME_ATTRIBUTE = 'data-theme';

export class ThemeManager {
    constructor() {
        this.currentTheme = null;
        this.systemThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    }

    /**
     * Initialize theme manager
     * Should be called on app startup
     */
    initialize() {
        console.log('[ThemeManager] Initializing');
        
        // Listen for system theme changes
        this.systemThemeMediaQuery.addEventListener('change', (e) => {
            console.log('[ThemeManager] System theme changed:', e.matches ? 'dark' : 'light');
            if (this.currentTheme === 'auto') {
                this.applyTheme('auto');
            }
        });

        // Load saved theme or default to auto
        const savedTheme = this.getSavedTheme();
        this.applyTheme(savedTheme);
    }

    /**
     * Get saved theme from localStorage
     * @returns {string} Theme value: 'light', 'dark', or 'auto'
     */
    getSavedTheme() {
        try {
            const saved = localStorage.getItem(THEME_KEY);
            if (saved && ['light', 'dark', 'auto'].includes(saved)) {
                console.log('[ThemeManager] Loaded saved theme:', saved);
                return saved;
            }
        } catch (error) {
            console.error('[ThemeManager] Error loading saved theme:', error);
        }
        console.log('[ThemeManager] No saved theme, defaulting to light');
        return 'light';
    }

    /**
     * Save theme preference to localStorage
     * @param {string} theme - Theme value: 'light', 'dark', or 'auto'
     */
    saveTheme(theme) {
        try {
            localStorage.setItem(THEME_KEY, theme);
            console.log('[ThemeManager] Saved theme:', theme);
        } catch (error) {
            console.error('[ThemeManager] Error saving theme:', error);
        }
    }

    /**
     * Get the system's preferred color scheme
     * @returns {string} 'dark' or 'light'
     */
    getSystemTheme() {
        return this.systemThemeMediaQuery.matches ? 'dark' : 'light';
    }

    /**
     * Resolve the actual theme to apply
     * @param {string} theme - Theme value: 'light', 'dark', or 'auto'
     * @returns {string} Resolved theme: 'light' or 'dark'
     */
    resolveTheme(theme) {
        if (theme === 'auto') {
            return this.getSystemTheme();
        }
        return theme;
    }

    /**
     * Apply theme to the document
     * @param {string} theme - Theme value: 'light', 'dark', or 'auto'
     */
    applyTheme(theme) {
        console.log('[ThemeManager] Applying theme:', theme);
        
        this.currentTheme = theme;
        this.saveTheme(theme);

        // Resolve auto to actual theme
        const resolvedTheme = this.resolveTheme(theme);
        console.log('[ThemeManager] Resolved theme:', resolvedTheme);

        // Apply theme to document
        const html = document.documentElement;
        
        if (resolvedTheme === 'dark') {
            html.setAttribute(THEME_ATTRIBUTE, 'metalark-dark');
            html.classList.add('dark');
        } else {
            html.setAttribute(THEME_ATTRIBUTE, 'metalark');
            html.classList.remove('dark');
        }

        console.log('[ThemeManager] Theme applied:', resolvedTheme);
    }

    /**
     * Get current theme
     * @returns {string} Current theme: 'light', 'dark', or 'auto'
     */
    getCurrentTheme() {
        return this.currentTheme || this.getSavedTheme();
    }

    /**
     * Get resolved theme (actual light/dark value)
     * @returns {string} Resolved theme: 'light' or 'dark'
     */
    getResolvedTheme() {
        return this.resolveTheme(this.getCurrentTheme());
    }
}

// Export singleton instance
export const themeManager = new ThemeManager();